import { useEffect, useId, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  OPEN_CONSENT_EVENT,
  acceptAll,
  getConsent,
  hasAnswered,
  rejectAll,
  setConsent,
} from "../lib/consent";

const FOCUS =
  "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-lemon";

/**
 * First-visit CMP + preferences panel. Mount once in the app shell.
 */
export default function CookieBanner() {
  const titleId = useId();
  const panelRef = useRef(null);
  // Eager first paint — do not wait on useEffect (avoids missing CMP on slow mounts).
  const [visible, setVisible] = useState(() => {
    if (typeof window === "undefined") return false;
    return !hasAnswered();
  });
  const [customize, setCustomize] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    if (!hasAnswered()) {
      setVisible(true);
    }

    const onOpen = () => {
      const current = getConsent();
      setAnalytics(Boolean(current?.analytics));
      setMarketing(Boolean(current?.marketing));
      setCustomize(true);
      setVisible(true);
    };

    window.addEventListener(OPEN_CONSENT_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_CONSENT_EVENT, onOpen);
  }, []);

  useEffect(() => {
    if (!visible || !panelRef.current) return;
    const first = panelRef.current.querySelector(
      "button, [href], input, select, textarea",
    );
    if (first instanceof HTMLElement) first.focus();
  }, [visible, customize]);

  // Escape closes only when reopening preferences (already answered) —
  // first-visit CMP must not dismiss without an explicit choice.
  useEffect(() => {
    if (!visible || !hasAnswered()) return undefined;
    const onKeyDown = (event) => {
      if (event.key !== "Escape") return;
      setVisible(false);
      setCustomize(false);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [visible]);

  if (!visible) return null;

  const closeAfter = (fn) => {
    fn();
    setVisible(false);
    setCustomize(false);
  };

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-[80] p-4 md:p-6 pointer-events-none"
      role="presentation"
      data-testid="cookie-banner"
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="pointer-events-auto mx-auto max-w-3xl border border-warmborder bg-cream text-ink shadow-[0_-8px_32px_rgba(28,37,54,0.12)] motion-safe:animate-[cookie-banner-in_280ms_ease-out] motion-reduce:animate-none"
      >
        <div className="p-5 md:p-6">
          <h2
            id={titleId}
            className="font-display italic text-2xl md:text-3xl text-navy leading-tight"
          >
            Preferenze cookie
          </h2>
          <p className="mt-3 text-sm md:text-base text-ink/80 leading-relaxed">
            Usiamo solo storage tecnico necessario al sito. Eventuali analitici
            o marketing si attivano solo con il tuo consenso. Dettagli nella{" "}
            <Link to="/cookie" className={`underline underline-offset-2 ${FOCUS} rounded-sm`}>
              Cookie policy
            </Link>
            .
          </p>

          {customize ? (
            <div className="mt-5 space-y-4 border-t border-warmborder pt-5">
              <label className="flex items-start gap-3 text-sm">
                <input
                  type="checkbox"
                  checked
                  disabled
                  className="mt-1"
                  aria-describedby="cookie-necessary-desc"
                />
                <span>
                  <span className="font-medium text-navy">Necessari</span>
                  <span id="cookie-necessary-desc" className="block text-ink/70 mt-0.5">
                    Sempre attivi — preferenze consenso e funzionamento del sito.
                  </span>
                </span>
              </label>
              <label className="flex items-start gap-3 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={analytics}
                  onChange={(e) => setAnalytics(e.target.checked)}
                  className={`mt-1 ${FOCUS}`}
                />
                <span>
                  <span className="font-medium text-navy">Analitici</span>
                  <span className="block text-ink/70 mt-0.5">
                    Statistiche aggregate, solo se abilitati in configurazione e
                    con consenso.
                  </span>
                </span>
              </label>
              <label className="flex items-start gap-3 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={marketing}
                  onChange={(e) => setMarketing(e.target.checked)}
                  className={`mt-1 ${FOCUS}`}
                />
                <span>
                  <span className="font-medium text-navy">Marketing</span>
                  <span className="block text-ink/70 mt-0.5">
                    Pixel di marketing di terze parti (oggi non in uso).
                  </span>
                </span>
              </label>
              <div className="flex flex-col sm:flex-row gap-3 pt-2">
                <button
                  type="button"
                  className={`flex-1 bg-navy text-cream px-4 py-3 text-sm font-medium hover:bg-navy-light transition-colors ${FOCUS}`}
                  onClick={() =>
                    closeAfter(() => setConsent({ analytics, marketing }))
                  }
                >
                  Salva preferenze
                </button>
                <button
                  type="button"
                  className={`flex-1 border border-navy text-navy px-4 py-3 text-sm font-medium hover:bg-cream-dark transition-colors ${FOCUS}`}
                  onClick={() => closeAfter(rejectAll)}
                >
                  Rifiuta non necessari
                </button>
              </div>
            </div>
          ) : (
            <div className="mt-5 flex flex-col sm:flex-row gap-3">
              <button
                type="button"
                className={`flex-1 bg-navy text-cream px-4 py-3 text-sm font-medium hover:bg-navy-light transition-colors ${FOCUS}`}
                onClick={() => closeAfter(acceptAll)}
              >
                Accetta tutti
              </button>
              <button
                type="button"
                className={`flex-1 border border-navy text-navy px-4 py-3 text-sm font-medium hover:bg-cream-dark transition-colors ${FOCUS}`}
                onClick={() => closeAfter(rejectAll)}
              >
                Rifiuta non necessari
              </button>
              <button
                type="button"
                className={`flex-1 border border-warmborder text-navy px-4 py-3 text-sm font-medium hover:bg-cream-dark transition-colors ${FOCUS}`}
                onClick={() => setCustomize(true)}
              >
                Personalizza
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
